<template>
  <div class="overflow-hidden pa-4 main-content w-100" style="height: calc(100vh - 23vh)">
    <v-row class="flex-nowrap align-center w-100" :class="{ 'mb-4': failedCreativeCount < 3 }"  v-if="failedCreativeCount < 3">
      <v-col cols="auto" class="ml-6 pr-2" style="width: 36px">
        <v-btn
          density="compact"
          icon="mdi-arrow-left"
          variant="plain"
          @click="goBack"
          :style="{ visibility: showBackButton && !areAllCreativesLoading ? 'visible' : 'hidden' }"
        />
      </v-col>

      <v-col cols="auto" class="py-0">
        <p class="text-start text-h6">
          {{
            areAllCreativesLoading
              ? $t('messages.generatingCreatives')
              : $t('messages.selectCreative')
          }}
        </p>
      </v-col>
      <v-spacer />
      <v-col cols="auto" class="mx-4 mr-16">
        <v-sheet
          class="d-flex align-center pa-1 border-sm gray-border-color cursor-pointer"
          rounded="lg"
          color="#f6f6f6"
          :style="{ visibility: isMoreLikeThisEnabled ? 'visible' : 'hidden' }"
        >
          <v-img
            :src="getReferenceImageUrl"
            alt="Product"
            class="product-image-custom mr-4"
            width="82"
            height="52"
          />
          <p class="text-h6 font-weight-medium mr-4">{{ $t('messages.moreLikeThis') }}</p>
        </v-sheet>
      </v-col>
    </v-row>
    <v-alert
      v-if="failedCreativeCount >= 3"
      density="compact"
      class="d-flex flex-column ml-15"
      style="width: fit-content"
      color="#f0f0f0"
    >
      <v-icon class="mr-2">mdi-information-outline</v-icon>
      <span>Creative generation was briefly interrupted due to an unknown issue.</span>
      <v-btn
        class="ml-2"
        size="small"
        prepend-icon="mdi-restore"
        color="#808080"
        rounded="xl"
        @click="retryCreativeGeneration"
      >
        Retry
      </v-btn>
    </v-alert>
    <v-row v-else class="flex-nowrap align-center w-100 pa-0">
      <v-col cols="auto">
        <v-btn
          icon
          variant="outlined"
          size="small"
          @click="previousSlide"
          :style="{
            visibility:
              creativeStore.currentSlide > 0 && makeCarouselArrowsVisible ? 'visible' : 'hidden',
          }"
        >
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
      </v-col>
      <v-col>
        <v-carousel
          v-model="creativeStore.currentSlide"
          :show-arrows="false"
          hide-delimiters
          height="auto"
          transition="slide-x-transition"
          reverse-transition="slide-x-reverse-transition"
          :transition-duration="500"
        >
          <v-carousel-item v-for="(group, index) in groupedCreatives" :key="index">
            <v-row class="flex-nowrap pa-0">
              <v-col
                cols="4"
                v-for="(creative, i) in group"
                :key="i"
                @mouseenter="hoveredCreativeId = creative.id"
                @mouseleave="hoveredCreativeId = null"
              >
                <v-sheet
                  :class="[
                    'border-md',
                    'pa-3',
                    'rounded-lg',
                    'd-flex',
                    'justify-center',
                    'align-center',
                    'position-relative',
                    selectedCreativeId === creative.id ? '' : 'gray-border-color',
                  ]"
                  :style="
                    selectedCreativeId === creative.id
                      ? {
                          'background-color': 'rgba(var(--v-theme-primary), 0.08)',
                          border: '2px solid rgb(var(--v-theme-primary)) !important',
                        }
                      : {}
                  "
                  @click="selectedCreativeId = creative.id"
                >
                  <v-responsive :aspect-ratio="1.59">
                    <v-container class="position-relative w-100 h-100 pa-0 ma-0">
                      <v-skeleton-loader
                        :loading="creative.isLoading"
                        type="image"
                        class="w-100 h-100"
                      >
                        <v-img
                          contain
                          v-if="creative.imageUrl"
                          :src="creative.imageUrl"
                          class="w-100 h-100"
                          @load="creative.isImageLoadedOnScreen = true"
                          @error="creative.imageUrl = ''"
                        />
                      </v-skeleton-loader>
                      <v-container
                        v-if="creative.isLoading"
                        class="pa-0 ma-0 position-absolute d-flex align-center justify-center w-100 h-100"
                        style="top: 0; left: 0; background-color: transparent; z-index: 2"
                      >
                        <v-sheet elevation="0" color="transparent">
                          <span class="text-subtitle-1 font-weight-medium">
                            {{ creative.loaderMessage }}
                          </span>
                        </v-sheet>
                      </v-container>
                    </v-container>
                  </v-responsive>
                </v-sheet>
                <v-row
                  class="justify-space-between align-center w-100 ma-0"
                  style="min-height: 36px"
                >
                  <v-btn
                    @click.stop="moreLikeThis(creative.id)"
                    variant="plain"
                    class="opacity-100 text-subtitle-1 text-none"
                    append-icon="mdi-creation"
                    v-if="hoveredCreativeId === creative.id && creative.imageUrl"
                    :style="{
                      visibility: isMoreLikeThisEnabled || isGenerating ? 'hidden' : 'visible',
                    }"
                  >
                    {{ $t('labels.moreLikeThis') }}
                  </v-btn>
                  <v-btn
                    @click.stop="customize(creative.id)"
                    variant="plain"
                    class="opacity-100 text-subtitle-1 text-none"
                    append-icon="mdi-pencil"
                    v-if="hoveredCreativeId === creative.id && creative.imageUrl"
                  >
                    {{ $t('labels.customize') }}
                  </v-btn>
                </v-row>
              </v-col>
            </v-row>
          </v-carousel-item>
        </v-carousel>
      </v-col>
      <v-col cols="auto">
        <v-btn
          icon
          variant="outlined"
          size="small"
          @click="nextSlide"
          :style="{
            visibility:
              creativeStore.currentSlide <
              Math.ceil(currentCreatives.length / noOfCreativesToBeShown) - 1
                ? 'visible'
                : 'hidden',
          }"
        >
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-row
      v-if="failedCreativeCount < 3"
      class="d-flex justify-center align-center"
      style="margin-top: 30px"
    >
      <v-btn
        prepend-icon="mdi-creation"
        variant="tonal"
        color="primary"
        rounded="xl"
        @click="generateMore()"
        v-if="showMoreIdeasButton"
      >
        <template v-slot:prepend>
          <v-icon color="primary" />
        </template>
        {{ $t('labels.generateMore') }}
      </v-btn>
    </v-row>
  </div>
</template>
<script lang="ts" setup>
import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'

import { useCanvasStore } from '@/stores/canvas'
import { useProductStore } from '@/stores/product'
import { useBrandStore } from '@/stores/brand'
import { usePlacementStore } from '@/stores/placement'
import { useCreativeStore } from '@/stores/creative'
import { useAppStore } from '@/stores/app'
import { useAssetStore } from '@/stores/asset'
import { useEditorStore } from '@/stores/editor'

import { useEnhancementsStore } from '@/stores/enhancement'
import CreativeService from '@/services/creativeService'
import type { GenerateRequest } from '@/types/generateRequest'
import type { Product, ProductWithInfo } from '@/types/product'
import type { FooterConfigProvider } from '@/types/footerConfig'
import type { Creative } from '@/types/creative'
import { standardFontFamilies } from '@/constants/fonts'
import { TEXT_TYPE_NAMES, type TextTypeName } from '@/types/textTypeName'
import { discountTypes, squareAspectRatioBuffer } from '@/constants/canvas'
import s3Folders from '@/constants/s3Folders'
import { getContrastColor, uploadToS3 } from '@/helpers/commonHelper'
import { loadPlacements, renderProductForShadowInput } from '@/helpers/placementHelper'
import {
  USkeywords,
  handleUSToUKEnglish,
  reflowTextAfterScaling,
  reflowTextBasedOnCollisions,
} from '@/helpers/textHelper'
import {
  countAspects,
  determineAvailablePlacements,
  getProductDimensions,
  suggestOptimalPlacement,
  type ProductData,
} from '@/helpers/productPlacementHelper'
import WebFont from 'webfontloader'
import { addDuoToneBackground, loadCanvasData, updateCTA } from '@/helpers/canvasHelper'
import { Canvas, FabricImage, FabricObject } from 'fabric'
import { v4 as uuidv4 } from 'uuid'
import {
  BACKDROP_TEXT_COLORS,
  AI_BACKGROUND_TEXT_COLOR,
  aiBgAllowedBrands,
  bathBackgrounds,
  getBackground,
  kitchenBackgrounds,
  overlays,
  specialBackgrounds,
  normalBackgrounds,
} from '@/helpers/backgroundHelper'
import { generateAllGoodCombinations, isAllFoodCategories, shuffle } from '@/helpers/productHelper'
import { getBestContrastingLogo, getImageAspectRatio } from '@/helpers/logoHelper'
import { getDominantColor, loadHtmlImage, showGlowLayer } from '@/helpers/imageHelper'
import { findCreativeInTree } from '@/helpers/creativeHelper'
import { TonalPalette, argbFromHex, hexFromArgb } from '@material/material-color-utilities'
import { rgbToHex } from '@/helpers/colorHelper'
import type { GenerateBgWithOverlayRequest } from '@/types/generateBgWithOverlayRequest'
import { PLACEMENT_TYPES } from '@/constants/positions'
import EditorService from '@/services/editorService'
import { useAdSizeConfigStore } from '@/stores/adSizeConfig'

const canvasStore = useCanvasStore()
const productStore = useProductStore()
const brandStore = useBrandStore()
const placementStore = usePlacementStore()
const creativeStore = useCreativeStore()
const appStore = useAppStore()
const assetStore = useAssetStore()
const enhancementsStore = useEnhancementsStore()
const editorStore = useEditorStore()
const adSizeConfigStore = useAdSizeConfigStore()
const creativeService = new CreativeService()
const editorService = new EditorService()
const router = useRouter()
const route = useRoute()
const { footerConfig, setFooterConfig } = inject<FooterConfigProvider>('footerConfig')!

const headlineHardLimit = computed(() => adSizeConfigStore.headlineUILimits.hardLimit)
const maxRetryCount = 5

const currentCreatives = ref<Creative[]>([])
const currentParentCreative = ref<Creative | null>(null)

const noOfCreativesToBeShown = ref<number>(3)
const showMoreIdeasButton = ref<boolean>(false)
const hoveredCreativeId = ref<string | null>(null)
const isMoreLikeThisEnabled = ref<boolean>(false)
const isGenerating = ref<boolean>(false)
const selectedCreativeId = ref<string | null>(null)
const loadingSelect = ref<boolean>(false)
const makeCarouselArrowsVisible = ref<boolean>(false)
const areAllCreativesLoading = ref<boolean>(false)
const backgroundGroups = ref<{ [key: string]: string[] }>({
  specialBackgrounds,
  normalBackgrounds,
  kitchenBackgrounds,
  bathBackgrounds,
})
const failedCreativeCount = ref<number>(0)

const showBackButton = computed(() => {
  return isMoreLikeThisEnabled.value || currentParentCreative.value !== null
})

const disableSelect = computed(() => {
  if (!selectedCreativeId.value || isGenerating.value) return true
  const creative = findCreativeInTree(
    creativeStore.creatives as Creative[],
    selectedCreativeId.value,
  )
  if (!creative?.imageUrl) return true
  return false
})

const getReferenceImageUrl = computed(() => {
  if (creativeStore.moreLikeThisReferenceId) {
    const referenceCreative = findCreativeInTree(
      creativeStore.creatives as Creative[],
      creativeStore.moreLikeThisReferenceId,
    )
    if (referenceCreative) {
      return referenceCreative.imageUrl
    }
  }
  return ''
})

const groupedCreatives = computed(() => {
  const groups = []
  const creatives = currentCreatives.value
  for (let i = 0; i < creatives.length; i += noOfCreativesToBeShown.value) {
    groups.push(creatives.slice(i, i + noOfCreativesToBeShown.value))
  }
  return groups
})

const nextSlide = () => {
  const maxSlides = Math.ceil(currentCreatives.value.length / noOfCreativesToBeShown.value) - 1
  if (creativeStore.currentSlide < maxSlides) {
    creativeStore.currentSlide++
  }
}

const previousSlide = () => {
  if (creativeStore.currentSlide > 0) {
    creativeStore.currentSlide--
  }
}

const moreLikeThis = async (creativeId: string) => {
  const creative = currentCreatives.value.find((c) => c.id === creativeId)
  if (!creative) return

  isMoreLikeThisEnabled.value = true
  creativeStore.moreLikeThisReferenceId = creative.id
  currentParentCreative.value = creative

  currentCreatives.value = JSON.parse(JSON.stringify(creative.subCreatives)) || []

  await generateCreatives(
    creative.logoUrl,
    creative.bgUrl,
    creative.isAIImageBackground,
    creative.id,
    creative.products.length,
  )
}

const generateMore = async () => {
  const refCreative = currentParentCreative.value
  showMoreIdeasButton.value = false

  await generateCreatives(
    refCreative?.logoUrl || '',
    refCreative?.bgUrl || '',
    refCreative?.isAIImageBackground,
    refCreative?.id || null,
    refCreative?.products.length || undefined,
  )
}

const goBack = () => {
  if (!currentParentCreative.value) return

  const parentId = currentParentCreative.value.parentId
  if (!parentId) {
    currentCreatives.value = creativeStore.creatives
    currentParentCreative.value = null
    isMoreLikeThisEnabled.value = false
    creativeStore.moreLikeThisReferenceId = ''
  } else {
    const grandParent = findCreativeInTree(creativeStore.creatives as Creative[], parentId)
    if (grandParent) {
      currentCreatives.value = grandParent.subCreatives || []
      currentParentCreative.value = grandParent
      isMoreLikeThisEnabled.value = true
      creativeStore.moreLikeThisReferenceId = grandParent.id
    }
  }
  creativeStore.currentSlide = currentCreatives.value.length / 3 - 1
}

const customize = (creativeId: string) => {
  finalizeStoreData(creativeId)
  router.push({ name: 'Editor' })
}

const goToDownloadPage = async () => {
  loadingSelect.value = true
  const creative = findCreativeInTree(
    creativeStore.creatives as Creative[],
    selectedCreativeId.value || '',
  )
  if (!creative) return
  const blobData = await fetch(creative?.imageUrl).then((res) => res.blob())

  const file = new File([blobData], `Generated-Creative-${Date.now()}.png`, { type: 'image/png' })
  const s3Url = await uploadToS3(file, s3Folders.generatedCreatives, file.name)
  assetStore.setCreativeS3Url(s3Url)
  if (selectedCreativeId.value) {
    finalizeStoreData(selectedCreativeId.value)
  }
  appStore.incrementStepperCount()
  router.push({ name: 'Download' })
  loadingSelect.value = false
}

const generateCreatives = async (
  logoUrl?: string,
  bgUrl?: string,
  isReferenceHasAIBg?: boolean,
  parentId: string | null = null,
  productLength?: number,
) => {
  isGenerating.value = true
  showMoreIdeasButton.value = false
  makeCarouselArrowsVisible.value = false
  areAllCreativesLoading.value = true
  creativeStore.currentSlide = currentCreatives.value.length / 3
  const batch = getBatch(isMoreLikeThisEnabled.value ? productLength : undefined)

  if (batch.length === 0) {
    console.info('No more products available for creative generation')
    return
  }

  const allProducts: any[] = []

  batch.forEach((products) => {
    products.forEach((product) => {
      if (!allProducts.find((p) => p.id == product.id)) {
        allProducts.push(product)
      }
    })
  });

  const generateShadowPromises = allProducts.map((product) => {
    return generateShadow(product);
  });

  const creatives: Creative[] = []

  batch.map((products) => {
    const id = uuidv4()
    const creative: Creative = {
      id,
      imageUrl: '',
      canvas: null,
      canvasJson: '',
      placementData: null,
      logoUrl: logoUrl || '',
      bgColor: '',
      bgColors: [],
      bgUrl: bgUrl || '',
      products: products,
      textColor: '',
      isFoodCategory: false,
      enhancements: [],
      isAIImageBackground: isReferenceHasAIBg || false,
      subCreatives: [],
      parentId,
      aiTextureUrl: '',
      overlayColor: '',
      isLoading: true,
      isImageLoadedOnScreen: false,
      availablePlacements: [],
      loaderMessage: '',
      currentRetryCount: 0,
    }

    creativeStore.addCreative(creative)
    if (!currentCreatives.value.some((c) => c.id === creative.id)) {
      currentCreatives.value.push(creative)
    }

    creatives.push(creative)
  })

  setTimeout(() => {
    if (!makeCarouselArrowsVisible.value) {
      makeCarouselArrowsVisible.value = true
    }
  }, 3000)

  await Promise.all(generateShadowPromises)

  const generatePromises = creatives.map((creative) => {
    return generateCreative(creative)
  })

  await Promise.all(generatePromises)
  isGenerating.value = false
  areAllCreativesLoading.value = false
  if (!showMoreIdeasButton.value) {
    setTimeout(() => {
      showMoreIdeasButton.value = true
    }, 3000)
  }
}

const generateCreative = async (currentCreative: Creative) => {
  try {
    let bgUrl = currentCreative.bgUrl
    let logoUrl = currentCreative.logoUrl
    let isAIImageBackground = false
    if (isMoreLikeThisEnabled.value) {
      bgUrl = getRandomFromSameGroup(bgUrl) ?? bgUrl
      currentCreative.bgUrl = bgUrl
    }

    const adSize = placementStore.selectedPlacement?.creativeSetGuidelines.ad_size
    const defaultSize = adSizeConfigStore.canvasSize
    const fabricCanvas = new Canvas(undefined, {
      width: adSize?.width || defaultSize.width,
      height: adSize?.height || defaultSize.height,
      preserveObjectStacking: true,
      selection: false,
    })
    if (placementStore.selectedPublishedVariant?.canvasJson) {
      await loadCanvasData(
        fabricCanvas,
        placementStore.selectedPublishedVariant.canvasJson,
        '#FFFFFF',
      )
    }

    const isFoodCategory = isAllFoodCategories(currentCreative.products)
    const bgUrls: string[] = []

    if (!(currentCreative.logoUrl && bgUrl)) {
      const creativeIndex = creativeStore.creatives.findIndex((c) => c.id === currentCreative.id)
      bgUrl = getBackground(creativeIndex)
      while (bgUrls.includes(bgUrl)) {
        bgUrl = getBackground(creativeIndex)
      }
      bgUrls.push(bgUrl)
      const logoIndex = 0
      logoUrl = brandStore.selectedBrand?.logoUrls[logoIndex].logoUrl || ''
    } else if (bgUrl && currentCreative.isAIImageBackground) {
      bgUrl = ''
    }
    const requestData = buildGenerateRequest(bgUrl, logoUrl, currentCreative.products, isFoodCategory)
    const response = await creativeService.generateCreative(requestData)
    const responseJson = JSON.parse(response.canvasJson)
    extendToObject()
    if (!bgUrl) {
      isAIImageBackground = true
      bgUrl = responseJson.backgroundImage?.src
    }

    // Setup placement data
    let productDataItems = currentCreative.products.map((product) => {
      const productData = product.productInfo.productData
      const fabricImage = product.productInfo.fabricImage as FabricImage
      return { productData, fabricImage }
    })
    productDataItems = JSON.parse(JSON.stringify(productDataItems))
    const isSpecialBackground = specialBackgrounds.includes(bgUrl)
    const restrictPlacements = isAIImageBackground || isSpecialBackground
    const { suggestedPlacement, availablePlacements } = await setupPlacementData(
      productDataItems,
      restrictPlacements,
    )

    const headlineObj = responseJson.objects.find((obj: any) => obj.layerName === 'Headline')
    if (headlineObj) {
      if (headlineObj.text.length > headlineHardLimit.value) {
        RegenerateHeadline(fabricCanvas)
      }
    }

    const clonedImages = (await Promise.all(
      currentCreative.products.map(async (p) => {
        const product: any = await p.productInfo.fabricImage.clone()
        product.aspectType = p.productInfo.productData.aspectType

        if (p.metaData) {
          product.isMultiple = JSON.parse(p.metaData)['isMultiple']
        }
        product.productId = p.id

        return product
      }),
    )) as FabricImage[]

    // Load products into canvas
    await loadPlacements(
      fabricCanvas,
      clonedImages,
      suggestedPlacement.placement,
      suggestedPlacement.mainShadow,
      suggestedPlacement.lightDirection,
      0,
      (suggestedPlacement as any).aspectCounts,
      false,
      isFoodCategory,
    )

    let textColor = ''
    const bgColor = response.primaryColor
    let aiTextureUrl = ''
    let overlayColor = ''
    let aiTextureOverlayUrl = ''

    if (isAIImageBackground) {
      ;({ aiTextureUrl, overlayColor, aiTextureOverlayUrl } = await updateAIImageBackground(
        fabricCanvas,
        bgUrl,
      ))
      if (fabricCanvas.backgroundImage) {
        fabricCanvas.backgroundImage.selectable = true
        fabricCanvas.backgroundImage.evented = false
      }
      textColor = AI_BACKGROUND_TEXT_COLOR
      if (aiTextureOverlayUrl !== '') {
        responseJson.backgroundImage.src = aiTextureOverlayUrl
        //bgUrl = aiTextureOverlayUrl
      }
    } else {
      textColor = getContrastColor(bgColor, BACKDROP_TEXT_COLORS)
    }
    canvasStore.textColor = textColor

    const objects = fabricCanvas.getObjects()

    //update logo and text based on contrast with background
    const bgColorToUse = isAIImageBackground ? overlayColor : bgColor
    const logos = brandStore.selectedBrand?.logoUrls.map((logo) => logo.logoUrl) || []
    if (!logos) return
    if (logos.length > 1 && !isMoreLikeThisEnabled.value) {
      const highContrastLogo = await getBestContrastingLogo(bgColorToUse, logos)
      if (highContrastLogo && highContrastLogo != logoUrl) {
        logoUrl = highContrastLogo
      }
    }
    await updateLogo(logoUrl, bgColorToUse, fabricCanvas, responseJson)

    updateTextLayerFromResponse(objects, responseJson, 'Headline', textColor)
    updateTextLayerFromResponse(objects, responseJson, 'SubHeadline', textColor, true)
    // Update headline & subheadline in memory → must render canvas to apply changes on canvas so trigger reflow will work correctly
    fabricCanvas.renderAll()

    // Get final canvas JSON and update with response data
    let finalCanvasJson = editorService.getCanvasJsonWithCustomInfo(fabricCanvas)
    updateCanvasObjectsFromResponse(finalCanvasJson, responseJson)
    updateCTA(fabricCanvas, finalCanvasJson, isFoodCategory, textColor, bgColorToUse)
    await loadCanvasData(fabricCanvas, finalCanvasJson, bgColorToUse)
    /*
     * Run text reflow after loading the new canvas data.
     * This ensures the headline and subheadline are recalculated against the latest object state.
     * Without this, reflow might sometimes run on stale or not-yet-updated data, causing inconsistencies.
     */
    fabricCanvas.renderAll()
    editorService.triggerTextReflow(fabricCanvas)
    finalCanvasJson = editorService.getCanvasJsonWithCustomInfo(fabricCanvas)
    if (requestData.baseBgImageUrl == specialBackgrounds[0]) {
      await addDuoToneBackground(fabricCanvas, bgColor, specialBackgrounds[1])
    }

    const dataUrl = fabricCanvas.toDataURL({ format: 'png', multiplier: 1 })
    const creative = {
      id: currentCreative.id,
      imageUrl: dataUrl,
      canvas: fabricCanvas,
      canvasJson: JSON.stringify(finalCanvasJson),
      placementData: suggestedPlacement,
      logoUrl,
      bgColor,
      bgColors: response.backgroundColors || [],
      bgUrl,
      products: currentCreative.products,
      textColor: textColor,
      isFoodCategory: isFoodCategory,
      enhancements: response.relatedItems || [],
      isAIImageBackground,
      subCreatives: [],
      parentId: currentCreative.parentId,
      aiTextureUrl: aiTextureUrl,
      overlayColor,
      isLoading: false,
      isImageLoadedOnScreen: false,
      availablePlacements,
      loaderMessage: currentCreative.loaderMessage,
      currentRetryCount: currentCreative.currentRetryCount,
    }
    creativeStore.updateCreative(currentCreative.id, currentCreative.parentId, creative)
    if (creative.id) {
      const index = creativeStore.creatives.findIndex((c) => c.id === creative.id)
      const updated = { ...creativeStore.creatives[index], isLoading: false }
      currentCreatives.value[index] = updated
    }
  } catch (error) {
    const index = currentCreatives.value.findIndex((c) => c.id === currentCreative.id)
    const updatedRetryCount = currentCreatives.value[index]?.currentRetryCount + 1
    const updatedCreative = {
      ...currentCreatives.value[index],
      loaderMessage:
        updatedRetryCount < 5 ? 'Taking a little longer than usual…' : 'We are working on it...',
      currentRetryCount: updatedRetryCount,
    }
    currentCreatives.value[index] = updatedCreative
    if (updatedRetryCount < 5) {
      handleRetryForFailedCreative(updatedCreative as Creative)
    }
    makeCarouselArrowsVisible.value = true
    if (updatedRetryCount >= 5) {
      failedCreativeCount.value += 1
    }
    console.error(error)
    console.error('Failed to generate creative')
  } finally {
    const hasAtLeastOneSuccess = currentCreatives.value.slice(-3).some((c) => c.imageUrl)
    if (hasAtLeastOneSuccess) {
      showMoreIdeasButton.value = true
    }
    const parentCreative = creativeStore.creatives.find((obj: any) => obj.id == currentCreative.parentId)

    if (parentCreative) {
      const storeCreative = creativeStore.creatives.find((c: any) => c.id === parentCreative.id)

      if (storeCreative && storeCreative.subCreatives) {
        // Take the last 3 subCreatives
        const lastThree = currentCreatives.value.slice(-3)

        // Check if all of them failed
        const allFailed = lastThree.every((sub: any) => sub.currentRetryCount === maxRetryCount)

        if (allFailed) {
          // Remove those last 3
          storeCreative.subCreatives.splice(-3, 3)
          currentCreatives.value = currentCreatives.value.filter(
            (c: any) => !lastThree.some((failed: any) => failed.id === c.id),
          )
        }
      }
    } else {
      // Root-level creatives (no parent)
      const lastThree = currentCreatives.value.slice(-3)

      const allFailed = lastThree.every((obj: any) => obj.currentRetryCount === maxRetryCount)

      if (allFailed) {
        // Remove those last 3
        creativeStore.creatives.splice(-3, 3)
        currentCreatives.value = currentCreatives.value.filter(
          (c: any) => !lastThree.some((failed: any) => failed.id === c.id),
        )
      }
    }
  }
}

const generateShadow = async (
  product: ProductWithInfo
) => {
  const adSize = placementStore.selectedPlacement?.creativeSetGuidelines.ad_size

  // Setup placement data
  const clonedImage: any = await product.productInfo.fabricImage.clone()
  clonedImage.productId = product.id

  // Load product into canvas and generate shadow
  await renderProductForShadowInput(clonedImage, adSize?.width, adSize?.height)
}

const updateAIImageBackground = async (canvas: Canvas, bgUrl: string) => {
  const randomIndex = Math.floor(Math.random() * overlays.length)
  let overlayColor = ''
  const aiTextureUrl = overlays[randomIndex]
  let aiTextureOverlayUrl = ''
  if (randomIndex == 0) {
    const htmlImage = await loadHtmlImage(bgUrl)
    const dominantColor = getDominantColor(htmlImage)
    const hexColor = rgbToHex(dominantColor.r, dominantColor.g, dominantColor.b)
    overlayColor = hexColor
  } else {
    overlayColor = await editorService.getOverlayColor(canvas)
    aiTextureOverlayUrl = await creativeService.GenerateBackgroundWithOverlay(
      appStore.selectedBusinessProfileId,
      {
        brandName: brandStore.selectedBrand?.name || '',
        bgUrl,
        overlayUrl: aiTextureUrl,
        color: overlayColor,
      } as GenerateBgWithOverlayRequest,
    )
    await editorService.addBackgroundImage(canvas, aiTextureOverlayUrl)
  }
  return { aiTextureUrl, overlayColor, aiTextureOverlayUrl }
}

const handleRetryForFailedCreative = async (creative: Creative) => {
  const index = currentCreatives.value.findIndex((c) => c.id === creative.id)
  const updated = { ...currentCreatives.value[index], isLoading: true }
  currentCreatives.value[index] = updated
  await generateCreative(creative)
}

const getBatch = (productLength?: number): ProductWithInfo[][] => {
  const batchSize = 3
  let productCombinations
  if (productLength) {
    productCombinations = creativeStore.productCombinations.filter(
      (combination) => combination.length === productLength,
    )
    productCombinations = shuffle(productCombinations)
  } else {
    productCombinations = creativeStore.productCombinations
  }
  const maxGenerated = productCombinations.length
  const batch: ProductWithInfo[][] = []

  const allProducts = productStore.selectedProducts as ProductWithInfo[]

  for (let i = 0; i < batchSize; i++) {
    const creativeIndex = currentCreatives.value.length + i
    const combination = productCombinations[creativeIndex % maxGenerated]

    const productsInCombination = combination
      .map((id) => allProducts.find((product) => product.productInfo.productData.id === id))
      .filter((p): p is ProductWithInfo => !!p)

    batch.push(productsInCombination)
  }

  return batch
}

const finalizeStoreData = (creativeId: string) => {
  const creative = findCreativeInTree(creativeStore.creatives as Creative[], creativeId)
  if (creative) {
    editorStore.productCount = creative.products.length
    creativeStore.setSelectedCreative(creative)
    if (creative.placementData) {
      placementStore.selectedPlacementOption = creative.placementData.placement
      placementStore.basePlacementType = JSON.parse(
        JSON.stringify(creative.placementData.placement),
      )
      placementStore.defaultPlacement = JSON.parse(JSON.stringify(creative.placementData))
      placementStore.processedProductData = creative.products.map((product) => product.productInfo)
      placementStore.placementOptions = creative.availablePlacements || []
    }

    const isCreativeHasAIBackground = creative.isAIImageBackground
    const aiBackgroundImages = creative.isFoodCategory ? kitchenBackgrounds : bathBackgrounds
    const selectedBackgroundImage = isCreativeHasAIBackground ? '' : creative.bgUrl
    const selectedAIBackgroundImage = isCreativeHasAIBackground ? creative.bgUrl : ''
    if (
      brandStore.selectedBrand?.name &&
      aiBgAllowedBrands.includes(brandStore.selectedBrand?.name)
    ) {
      placementStore.setAIBackgroundImages(aiBackgroundImages)
    } else {
      placementStore.setAIBackgroundImages([])
    }

    placementStore.setSelectedBackgroundImage(selectedBackgroundImage)
    placementStore.setSelectedAIBackgroundImage(selectedAIBackgroundImage)
    placementStore.setBaseBackgroundImage(selectedBackgroundImage)
    placementStore.setBaseAIBackgroundImage(selectedAIBackgroundImage)
    if (creative.isAIImageBackground) {
      placementStore.setOverlayImage(creative.aiTextureUrl)
    }

    //For AI images, bgColor is texture color which can have grey shades based on the background.
    const color = creative.isAIImageBackground ? creative.bgColors[0] : creative.bgColor
    brandStore.setSelectedColor(color)
    brandStore.colours = brandStore.selectedBrand?.colours.map((item) => item) ?? []
    const brandLogo = brandStore.logos.find((logo) => logo.logoUrl === creative.logoUrl)
    if (brandLogo) {
      brandStore.setBaseData(brandLogo, creative.bgColor, creative.bgColors)
    }

    productStore.setBaseProducts(creative.products)

    canvasStore.setCanvasJson(JSON.parse(creative.canvasJson))
    canvasStore.setLogoUrl(creative.logoUrl)
    canvasStore.setTextColor(creative.textColor)
    canvasStore.setAreAllEdibleProducts(creative.isFoodCategory)
    const canvasJson = JSON.parse(creative.canvasJson)
    canvasStore.setEnableResetCanvas(true, canvasJson, canvasJson.backgroundImage?.src || '')

    const enhancingItems = creative.enhancements.map((itemName: string) => {
      const key = `${creative.bgColor}_${selectedBackgroundImage || selectedAIBackgroundImage}`
      return {
        name: itemName,
        apply: {},
        remove: { [key]: canvasJson.backgroundImage?.src || '' },
      }
    })
    enhancementsStore.setEnhancingItems(enhancingItems)
  }
}

const buildGenerateRequest = (
  bgUrl: string,
  logoUrl: string,
  products: Product[],
  isFoodCategory: boolean,
): GenerateRequest => ({
  brandName: brandStore.selectedBrand?.name || '',
  logoUrl: logoUrl,
  productUrls: products.map((product: Product) => product.productUrl),
  canvasJson: JSON.stringify(placementStore.selectedPublishedVariant?.canvasJson),
  guidelinesData: JSON.stringify(placementStore.selectedPlacement?.creativeSetGuidelines),
  ...(bgUrl && { baseBgImageUrl: bgUrl }),
  primaryColor: null,
  discountInfo: JSON.stringify({
    isEnabled: canvasStore.discountType != discountTypes.none,
    type: canvasStore.discountType,
    variation: canvasStore.discountVariation,
    value: canvasStore.discountValue,
    couponCode: canvasStore.rawCouponCode?.toUpperCase(),
    currencyValue: appStore.profileCurrencySymbol,
  }),
  label: brandStore.activeLabel.label,
  brief: brandStore.activeLabel.brief,
  isFoodCategory,
})

const setupPlacementData = async (
  productDataItems: { productData: ProductData; fabricImage: FabricImage }[],
  restrictPlacements: boolean,
) => {
  const aspectCounts = countAspects(productDataItems.map((p) => p.productData))
  const availablePlacements = determineAvailablePlacements(
    productDataItems.length,
    aspectCounts,
    productDataItems.map((p) => p.productData),
  )
  let placements = availablePlacements
  if (
    restrictPlacements &&
    ((productDataItems.length == 2 && aspectCounts.Horizontal == 2) ||
      (productDataItems.length == 3 && aspectCounts.Horizontal == 3))
  ) {
    placements = [PLACEMENT_TYPES.CORNER]
  } else if (restrictPlacements) {
    placements = availablePlacements.filter(
      (p) =>
        p === PLACEMENT_TYPES.FRONT ||
        p === PLACEMENT_TYPES.LAYER ||
        p === PLACEMENT_TYPES.STAIRCASE,
    )
  }
  const suggestedPlacement = await suggestOptimalPlacement(
    productDataItems.map((p) => p.productData),
    placements,
    false,
  )

  return { suggestedPlacement, availablePlacements }
}

const updateLogo = async (
  logoUrl: string,
  bgColor: string,
  fabricCanvas: Canvas,
  responseJson: any,
) => {
  const objects = fabricCanvas.getObjects() as FabricObject[]
  const defaultJson = placementStore.selectedPublishedVariant?.canvasJson
  let isRectangleLogo = false
  try {
    const aspectRatio = await getImageAspectRatio(logoUrl)
    if (aspectRatio >= squareAspectRatioBuffer) {
      isRectangleLogo = true
    }
  } catch (e) {
    console.error('Error calculating the aspect ratio of logo:', e)
  }
  const defaultLogoObj = isRectangleLogo
    ? defaultJson?.objects.find((obj: any) => obj.layerName === 'Logo - Horizontal')
    : defaultJson?.objects.find((obj: any) => obj.layerName === 'Logo')

  if (!defaultLogoObj) {
    console.error('No default logo object found in JSON.')
    return
  }

  try {
    const newImage = await FabricImage.fromURL(logoUrl, { crossOrigin: 'anonymous' })

    const defaultWidth = defaultLogoObj.width * defaultLogoObj.scaleX
    const defaultHeight = defaultLogoObj.height * defaultLogoObj.scaleY

    const scaleX = defaultWidth / newImage.width!
    const scaleY = defaultHeight / newImage.height!
    const scale = Math.min(scaleX, scaleY)

    const logoSquareObj = objects.find((obj: any) => obj.layerName === 'Logo') as FabricImage
    const logoHorizontalObj = objects.find(
      (obj: any) => obj.layerName === 'Logo - Horizontal',
    ) as FabricImage

    const logoSquareLayer = responseJson.objects.find((obj: any) => obj.layerName === 'Logo')
    const logoHorizontalLayer = responseJson.objects.find(
      (obj: any) => obj.layerName === 'Logo - Horizontal',
    )
    let logoObj
    if (isRectangleLogo) {
      logoObj = logoHorizontalObj
      logoSquareObj.visible = false
      logoSquareLayer.visible = false
      logoHorizontalObj.visible = true
      logoHorizontalLayer.visible = true
    } else {
      logoObj = logoSquareObj
      logoHorizontalObj.visible = false
      logoHorizontalLayer.visible = false
      logoSquareObj.visible = true
      logoSquareLayer.visible = true
    }

    if (logoObj) {
      logoObj.setElement(newImage.getElement())
      logoObj.set({
        scaleX: scale,
        scaleY: scale,
      })
      logoObj.setCoords()
      await showGlowLayer(fabricCanvas, logoUrl, bgColor)

      const responseJsonLogo = isRectangleLogo ? logoHorizontalLayer : logoSquareLayer
      if (responseJsonLogo) {
        responseJsonLogo.src = logoUrl
        responseJsonLogo.scaleX = scale
        responseJsonLogo.scaleY = scale
        responseJsonLogo.width = logoObj.width
        responseJsonLogo.height = logoObj.height
      }
    }
  } catch (error) {
    console.error('Error loading image:', error)
  }
}

const updateTextLayerFromResponse = (
  objects: FabricObject[],
  responseJson: any,
  layerName: string,
  textColor: string,
  applyBold: boolean = false,
) => {
  const currentObj = objects.find((obj: any) => obj.layerName === layerName)
  const responseObj = responseJson.objects.find((obj: any) => obj.layerName === layerName)
  if (
    responseObj.text &&
    USkeywords.some((word) => responseObj.text.toLowerCase().includes(word))
  ) {
    responseObj.text = handleUSToUKEnglish(responseObj.text)
  }
  if (currentObj && responseObj) {
    currentObj.set({
      text: responseObj.text,
      left: responseObj.left,
      top: responseObj.top,
      fontSize: responseObj.fontSize,
      maxLines: responseObj.maxLines,
      scaleX: responseObj.scaleX,
      scaleY: responseObj.scaleY,
      angle: responseObj.angle,
      fill: textColor,
      fontWeight: responseObj.fontWeight,
      fontFamily: responseObj.fontFamily,
      styles: responseObj.styles,
      dirty: true, // force re-render
    })

    if (applyBold) {
      applyBoldToDiscountCode(currentObj)
    }

    currentObj.setCoords()
  }
}

const applyBoldToDiscountCode = (layerObj: any) => {
  layerObj.styles = []
  if (canvasStore.rawCouponCode) {
    const couponCode = canvasStore.rawCouponCode.toUpperCase()
    const simulatedTextLines = layerObj.text?.split('\n')
    const lineIndex = simulatedTextLines?.findIndex((line: any) => line.includes(couponCode))
    if (lineIndex != undefined && lineIndex != -1 && simulatedTextLines) {
      const currentLine = simulatedTextLines[lineIndex]
      const startCharIndex = currentLine.indexOf(couponCode)
      const endCharIndex = startCharIndex + couponCode.length - 1
      for (let i = startCharIndex; i <= endCharIndex; i++) {
        layerObj.styles[lineIndex] = layerObj.styles[lineIndex] || {}
        layerObj.styles[lineIndex][i] = { fontWeight: 'bold' }
      }
    }
  }
}

const updateCanvasObjectsFromResponse = (canvasJson: any, responseJson: any) => {
  const updateObject = (layerName: string) => {
    const currentObj = canvasJson.objects.find((obj: any) => obj.layerName === layerName)
    const responseObj = responseJson.objects.find((obj: any) => obj.layerName === layerName)

    if (currentObj && responseObj) {
      const objIndex = canvasJson.objects.indexOf(currentObj)
      canvasJson.objects[objIndex] = responseObj
    }
  }

  // Update all canvas objects
  updateObject('Logo')

  // Update background image
  if (responseJson.backgroundImage) {
    canvasJson.backgroundImage = responseJson.backgroundImage
  }
}

const RegenerateHeadline = async (fabricCanvas: Canvas) => {
  try {
    if (!appStore.selectedBusinessProfileId || !brandStore.selectedBrand) return
    const charLimits = adSizeConfigStore.headlineCharLimits
    const response = await creativeService.writeUsingAI(appStore.selectedBusinessProfileId, {
      prompt:
        'Write a catchy headline which describes the brand, products along with discount information very well',
      headlineType: TEXT_TYPE_NAMES.HEADLINE,
      brandName: brandStore.selectedBrand?.name,
      productUrls: productStore.selectedProducts.map((product) => product.productUrl),
      minimumCharacters: charLimits.min,
      maximumCharacters: charLimits.max,
      discountInfo: JSON.stringify({
        isEnabled: canvasStore.discountType != discountTypes.none,
        type: canvasStore.discountType,
        variation: canvasStore.discountVariation,
        value: canvasStore.discountValue,
        couponCode: canvasStore.rawCouponCode?.toUpperCase(),
        currencyValue: appStore.profileCurrencySymbol,
      }),
    })
    response.headlines.forEach((newHeadline: string) => {
      if (newHeadline && USkeywords.some((word) => newHeadline.toLowerCase().includes(word))) {
        newHeadline = handleUSToUKEnglish(newHeadline)
      }
    })
    const generatedHeadlines = response.headlines
    const selectedHeadline =
      generatedHeadlines.find((h: any) => h.length < 25) || generatedHeadlines[0]
    updateHeadline(selectedHeadline, TEXT_TYPE_NAMES.HEADLINE, fabricCanvas)
  } catch {
    console.error('Failed to generate headlines using AI!')
  }
}

const updateHeadline = (newHeadline: string, textTypeName: TextTypeName, fabricCanvas: Canvas) => {
  if (newHeadline && USkeywords.some((word) => newHeadline.toLowerCase().includes(word))) {
    newHeadline = handleUSToUKEnglish(newHeadline)
  }
  updateCanvasJsonText(newHeadline, textTypeName, fabricCanvas)
}

const updateCanvasJsonText = (text: string, layerName: string, fabricCanvas: any) => {
  const allObjects = fabricCanvas.getObjects()
  const layerObj: any = allObjects.find((obj: any) => obj.layerName == layerName)
  if (!layerObj) return
  layerObj.previousHeight = layerObj.height
  if (layerName == 'SubHeadline') {
    const headline: any = allObjects.find((obj: any) => obj.layerName == 'Headline')
    headline.previousHeight = headline.height
  }
  const trimmedText = text.trim()
  layerObj.set('text', trimmedText)
  if (layerName === 'SubHeadline') {
    applyBoldToDiscountCode(layerObj)
  }

  const glowGroups = allObjects.filter((obj: any) => obj?.layerName?.includes('glowGroup'))
  if (glowGroups.length > 0) {
    reflowTextAfterScaling(fabricCanvas, glowGroups[0])
  } else {
    const products = allObjects.filter((obj: any) => obj.layerName?.includes('Product'))
    if (products.length > 0) {
      const sortedProducts = [...products].sort((a, b) => {
        const aLeft = a.getBoundingRect().left
        const bLeft = b.getBoundingRect().left
        return aLeft - bLeft
      })
      reflowTextBasedOnCollisions(fabricCanvas, sortedProducts)
    }
  }
  fabricCanvas.requestRenderAll()
}

const extendToObject = () => {
  FabricObject.prototype.toObject = (function (toObject) {
    return function (this: any) {
      return {
        ...toObject.call(this),
        productId: this.productId,
        layerId: this.layerId,
        layerName: this.layerName,
        layerType: this.layerType,
        isMultiple: this.isMultiple,
        autofit: this.autofit,
        maxLines: this.maxLines,
        text: this.text,
        fontSize: this.fontSize,
        fontWeight: this.fontWeight,
        fontFamily: this.fontFamily,
        fontStyle: this.fontStyle,
        lineHeight: this.lineHeight,
        editable: this.editable,
        textAlign: this.textAlign,
        imageFit: this.imageFit,
        positionIndex: this.positionIndex,
        hasOriginChanged: this.hasOriginChanged,
        deltaY: this.deltaY,
        renderAtLevel: this.renderAtLevel,
        aiShadowProperties: this.aiShadowProperties,
        rx: this.rx,
        ry: this.ry,
      }
    }
  })(FabricObject.prototype.toObject)
}

const preProcessProductData = async () => {
  const products = []
  for (let i = 0; i < productStore.selectedProducts.length; i++) {
    const product = productStore.selectedProducts[i]
    const dimensionsResult = await getProductDimensions(product.productUrl, i)
    if (dimensionsResult.fabricImage) {
      const productWithInfo: ProductWithInfo = {
        ...product,
        productInfo: {
          productData: dimensionsResult.productData,
          fabricImage: dimensionsResult.fabricImage,
          shadowInfo: null
        },
      }
      products.push(productWithInfo)
    }
  }
  productStore.setSelectedProducts(products)
}

const loadFonts = async () => {
  const data = placementStore.selectedPublishedVariant?.canvasJson
  const uniqueFonts: string[] = [
    ...new Set<string>(
      data.objects
        .filter(
          (obj: any) =>
            (obj.layerType === 'Text' || obj.type === 'Textbox') &&
            typeof obj.fontFamily === 'string',
        )
        .map((obj: { fontFamily: string }) => obj.fontFamily)
        .filter((font: any) => !standardFontFamilies.includes(font)),
    ),
  ]

  if (uniqueFonts.length) {
    await new Promise<void>((resolve) => {
      WebFont.load({
        google: { families: uniqueFonts },
        active: () => {
          resolve()
        },
        inactive: () => {
          console.warn('Some fonts failed to load. Falling back to default font.')
          resolve()
        },
      })
    })
  }
}

const getRandomFromSameGroup = (bgUrl: string) => {
  const group = Object.values(backgroundGroups.value).find((list) => list.includes(bgUrl))

  if (!group) {
    return null // not found in any group
  }

  // pick a random element from the same group
  const randomIndex = Math.floor(Math.random() * group.length)
  return group[randomIndex]
}

const retryCreativeGeneration = () => {
  failedCreativeCount.value = 0
  generateCreatives()
}

watch(disableSelect, (newValue: boolean) => {
  footerConfig.disableSelect = newValue
})

watch(loadingSelect, (newValue: boolean) => {
  footerConfig.loadingSelect = newValue
})

watch(
  () => currentCreatives.value.map((c) => ({ isImageLoadedOnScreen: c.isImageLoadedOnScreen })),
  (creativesStatusArr) => {
    const endIndex = currentCreatives.value.length - 1
    let noOfCreativesGeneratedInTheLastBatch = 3
    if (currentCreatives.value.length % 3 !== 0) {
      noOfCreativesGeneratedInTheLastBatch = currentCreatives.value.length % 3
    }
    const startIndex = endIndex - noOfCreativesGeneratedInTheLastBatch + 1
    const currentCreativesSlice = creativesStatusArr.slice(startIndex, endIndex + 1)
    if (currentCreativesSlice.some((creative) => !!creative.isImageLoadedOnScreen)) {
      areAllCreativesLoading.value = false
      currentCreatives.value.map((creative) => {
        if (!(creative.loaderMessage || creative.isImageLoadedOnScreen)) {
          creative.loaderMessage = 'Still working on the final touches…'
        }
      })
    }
  },
)

onBeforeRouteLeave((to, from, next) => {
  if (to.name === 'Mode') {
    const answer = window.confirm('You have unsaved changes. Are you sure you want to leave?')
    if (answer) {
      next()
    } else {
      next(false)
    }
  } else {
    next()
  }
})

onMounted(async () => {
  setFooterConfig({
    showSelect: true,
    disableSelect: disableSelect.value,
    loadingSelect: false,
    onSelect: goToDownloadPage,
    showBack: true,
  })
  window.onbeforeunload = () => {
    if (route.name === 'Generate') return ''
  }
  if (appStore.stepperCount != 1) {
    appStore.setStepperCount(1)
  }
  if (creativeStore.creatives.length == 0) {
    areAllCreativesLoading.value = true
    await loadFonts()
    await preProcessProductData()
    const productCombinations = generateAllGoodCombinations()
    creativeStore.setProductCombinations(productCombinations)
    generateCreatives()
  } else {
    if (creativeStore.moreLikeThisReferenceId) {
      isMoreLikeThisEnabled.value = true
      currentParentCreative.value = findCreativeInTree(
        creativeStore.creatives as Creative[],
        creativeStore.moreLikeThisReferenceId,
      )
      if (currentParentCreative.value) {
        currentCreatives.value = JSON.parse(
          JSON.stringify(currentParentCreative.value.subCreatives),
        )
      }
    } else {
      currentCreatives.value = JSON.parse(JSON.stringify(creativeStore.creatives))
    }
    makeCarouselArrowsVisible.value = true
    if (currentCreatives.value.every((c) => c.imageUrl)) {
      showMoreIdeasButton.value = true
    }
    canvasStore.fabricCanvas = null
  }
  canvasStore.setDiscountType(canvasStore.discountInput.type)
  canvasStore.setDiscountValue(canvasStore.discountInput.value)
  canvasStore.setRawCouponCode(canvasStore.discountInput.couponCode)
  canvasStore.setDiscountVariation(canvasStore.discountInput.variation)
  canvasStore.setBaseDiscountInfo({
    type: canvasStore.discountInput.type,
    value: canvasStore.discountInput.value,
    couponCode: canvasStore.discountInput.couponCode,
    variation: canvasStore.discountInput.variation,
  })
})

onUnmounted(() => {
  setFooterConfig({
    showSelect: false,
    disableSelect: false,
    loadingSelect: false,
    onSelect: () => {},
    showBack: false,
  })
  window.onbeforeunload = null
  canvasStore.setLastUsedDiscountInfo(canvasStore.baseDiscountInfo)
})
</script>
<style scoped lang="scss">
::v-deep(.v-skeleton-loader__image) {
  width: 100% !important;
  height: 100% !important;
}

.gray-border-color {
  border-color: #9b9a9a !important;
}

.product-image-custom {
  border-radius: 8px;
  flex-shrink: 0;
}

.error-bg {
  background-color: rgba(255, 0, 0, 0.05);
}
</style>
